// ./src/components/features/SavedPlaces/SavedPlacesList/SavedPlacesList.tsx
//
// List component for the Saved Places page.
// Renders saved place records as responsive cards with Framer Motion
// entrance animations — no table view.
//
// SavedPlaceCard is a self-contained card that visually mirrors RecordCard's
// layout conventions (spacing, font sizes, hover actions, swipe behavior)
// but handles saved-place-specific data: name, place type, address,
// coordinates, use count, privacy badge.
//
// Follows the same structural patterns as TravelList / LogbookList:
// - Context-vs-props resolution via useOptionalSavedPlacesContext()
// - Swipeable actions on mobile, hover actions on desktop
// - Entrance animations via useEntranceAnimation
// - LoadMoreButton for pagination
// - RecordCardSkeleton for loading state

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Checkbox,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import {
  SavedPlace,
  PLACE_TYPE,
  PlaceTypeValue,
  SAVED_PLACE_STATUS,
  formatSavedPlaceAddress,
} from "@/utils/api";
import { useOptionalSavedPlacesContext } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  useSwipeableList,
  RecordCardSkeleton,
  LoadMoreButton,
  SwipeableCard,
  SwipeAction,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

// ============================================
// Styles — matches RecordCard / TravelCard
// hover pattern
// ============================================

const ACTIONS_CLASS = "saved-place-card__actions";
const CARD_CLASS = "saved-place-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-saved-place-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Helpers
// ============================================

/**
 * Map place type to an icon name from @tabler/icons-react.
 */
const getPlaceTypeIcon = (placeType?: PlaceTypeValue): string => {
  switch (placeType) {
    case PLACE_TYPE.HOME:
      return "Home";
    case PLACE_TYPE.OFFICE:
      return "Building";
    case PLACE_TYPE.CLIENT:
      return "Users";
    case PLACE_TYPE.GAS_STATION:
      return "GasStation";
    case PLACE_TYPE.MECHANIC:
      return "Tool";
    case PLACE_TYPE.STORE:
      return "ShoppingCart";
    case PLACE_TYPE.OTHER:
    default:
      return "MapPin2";
  }
};

/**
 * Map place type to a badge color.
 */
const getPlaceTypeColor = (placeType?: PlaceTypeValue): string => {
  switch (placeType) {
    case PLACE_TYPE.HOME:
      return "blue";
    case PLACE_TYPE.OFFICE:
      return "cyan";
    case PLACE_TYPE.CLIENT:
      return "violet";
    case PLACE_TYPE.GAS_STATION:
      return "orange";
    case PLACE_TYPE.MECHANIC:
      return "red";
    case PLACE_TYPE.STORE:
      return "green";
    case PLACE_TYPE.OTHER:
    default:
      return "gray";
  }
};

// ============================================
// Types
// ============================================

export interface SavedPlacesListProps {
  /** Saved place records (falls back to context if not provided) */
  records?: SavedPlace[];

  /** Loading state (falls back to context) */
  loading?: boolean;

  /** Loading more state (falls back to context) */
  loadingMore?: boolean;

  /** Whether more items exist (falls back to context) */
  hasMore?: boolean;

  /** Load more callback (falls back to context) */
  onLoadMore?: () => void;

  /** Selected place IDs */
  selectedIds?: string[];

  /** Selection change callback */
  onSelectionChange?: (selectedIds: string[]) => void;

  /** Edit callback */
  onEdit?: (place: SavedPlace) => void;

  /** Delete callback */
  onDelete?: (place: SavedPlace) => void;

  /** Record click callback */
  onRecordClick?: (place: SavedPlace) => void;

  /** ID of place being deleted (falls back to context) */
  deletingId?: string | null;

  /** ID of highlighted place */
  highlightedId?: string | null;

  /** Add button callback (for empty state) */
  onAdd?: () => void;

  /** Enable selection checkboxes */
  selectable?: boolean;

  /** Search keyword to display in filtered-empty state */
  searchKeyword?: string;
}

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="sm">
      {Array.from({ length: 5 }).map((_, index) => (
        <RecordCardSkeleton
          key={index}
          showPrice={false}
          customContentRows={index % 2 === 0 ? 2 : 1}
        />
      ))}
    </Stack>
  );
};

// ============================================
// SavedPlaceCard
// ============================================

interface SavedPlaceCardProps {
  record: SavedPlace;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick?: (record: SavedPlace) => void;
  onEdit?: (record: SavedPlace) => void;
  onDelete?: (record: SavedPlace) => void;
  selectable: boolean;
  highlighted: boolean;
  isDeleting: boolean;
  // Swipe management
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const SavedPlaceCardComponent: React.FC<SavedPlaceCardProps> = ({
  record,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  selectable,
  highlighted,
  isDeleting,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["savedPlaces", "common"]);

  injectStyles();

  const placeTypeIcon = getPlaceTypeIcon(record.placeType);
  const placeTypeColor = getPlaceTypeColor(record.placeType);

  const hasAddress = Boolean(record.address1 || record.city || record.stateProvince || record.country);
  const hasWhereDone = Boolean(record.whereDone);
  const hasCoordinates = record.latitude != null && record.longitude != null;
  const hasUseCount = record.useCount != null && record.useCount > 0;
  const isInactive = record.status === SAVED_PLACE_STATUS.INACTIVE;
  const hasActions = Boolean(onEdit) || Boolean(onDelete);

  // Full formatted address
  const formattedAddress = useMemo(() => {
    if (!hasAddress) return null;
    return formatSavedPlaceAddress(record);
  }, [record, hasAddress]);

  // ── Event handlers ──

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (record.id) {
        onSelect(record.id, event.currentTarget.checked);
      }
    },
    [record.id, onSelect],
  );

  const handleClick = useCallback(() => {
    onClick?.(record);
  }, [onClick, record]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(record);
    },
    [onEdit, record],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(record);
    },
    [onDelete, record],
  );

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => {
      onSwipeStateChange?.(isOpen);
    },
    [onSwipeStateChange],
  );

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(record),
        disabled: isDeleting,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(record),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, record, isDeleting, t]);

  // ── Desktop hover actions ──

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Place type badge ──

  const placeTypeBadge = record.placeType && (
    <Badge variant="outline" size="sm" color={placeTypeColor}>
      <Group gap={4} wrap="nowrap">
        <AppIcon iconName={placeTypeIcon} size={12} />
        {t(`savedPlaces:placeTypes.${record.placeType}`, { defaultValue: record.placeType })}
      </Group>
    </Badge>
  );

  // ── Status / privacy badges ──

  const metaBadges = (
    <Group gap={6} wrap="wrap">
      {isInactive && (
        <Badge variant="light" size="sm" color="gray">
          {t("savedPlaces:status.inactive", { defaultValue: "Inactive" })}
        </Badge>
      )}
      {record.isPrivate && (
        <Badge variant="light" size="sm" color="yellow">
          <Group gap={4} wrap="nowrap">
            <AppIcon iconName="Lock" size={12} />
            {t("savedPlaces:labels.private", { defaultValue: "Private" })}
          </Group>
        </Badge>
      )}
    </Group>
  );

  // ── Content rows (shared mobile/desktop) ──

  const contentRows = (
    <>
      {/* Row: Place type badge + meta badges */}
      <Group gap="sm" wrap="wrap" align="center">
        {placeTypeBadge}
        {metaBadges}
        {hasUseCount && (
          <Group gap={4}>
            <AppIcon iconName="Click" size={16} color="gray" />
            <Text size="sm" c="dimmed">
              {t("savedPlaces:labels.useCount", {
                count: record.useCount,
                defaultValue: "Used {{count}} times",
              })}
            </Text>
          </Group>
        )}
      </Group>

      {/* Row: Where done (business/place name) */}
      {hasWhereDone && record.whereDone !== record.name && (
        <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <AppIcon iconName="BuildingStore" size={16} color="gray" style={{ flexShrink: 0 }} />
          <Text size="md" c="dimmed" lineClamp={1} style={{ minWidth: 0 }}>
            {record.whereDone}
          </Text>
        </Group>
      )}

      {/* Row: Full address */}
      {formattedAddress && (
        <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <AppIcon iconName="MapPin" size={16} color="gray" style={{ flexShrink: 0 }} />
          <Text size="md" c="dimmed" lineClamp={2} style={{ minWidth: 0 }}>
            {formattedAddress}
          </Text>
        </Group>
      )}

      {/* Row: Coordinates (only if no address) */}
      {hasCoordinates && !hasAddress && (
        <Group gap={6} wrap="nowrap" align="center">
          <AppIcon iconName="CurrentLocation" size={16} color="gray" style={{ flexShrink: 0 }} />
          <Text size="sm" c="dimmed">
            {record.latitude!.toFixed(5)}, {record.longitude!.toFixed(5)}
          </Text>
        </Group>
      )}
    </>
  );

  // ============================================
  // MOBILE LAYOUT (<sm)
  // ============================================

  const mobileLayout = (
    <Box hiddenFrom="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Checkbox */}
        {selectable && (
          <Box pt={3}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
              size="sm"
              aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
            />
          </Box>
        )}

        {/* Content */}
        <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
          {/* Header: Icon + Name */}
          <Group gap="xs" wrap="nowrap" align="center">
            <AppIcon
              iconName={placeTypeIcon}
              size={20}
              color={`var(--mantine-color-${placeTypeColor}-6)`}
              style={{ flexShrink: 0 }}
            />
            <Text size="md" fw={600} truncate="end" style={{ minWidth: 0 }}>
              {record.name}
            </Text>
          </Group>

          {/* Content rows */}
          {contentRows}
        </Stack>
      </Group>
    </Box>
  );

  // ============================================
  // DESKTOP LAYOUT (sm+)
  // ============================================

  const desktopLayout = (
    <Box visibleFrom="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Checkbox */}
        {selectable && (
          <Box pt={4}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
              size="md"
              aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
            />
          </Box>
        )}

        {/* Content */}
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {/* Header row: Icon + Name ... [Actions] */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
              <AppIcon
                iconName={placeTypeIcon}
                size={22}
                color={`var(--mantine-color-${placeTypeColor}-6)`}
                style={{ flexShrink: 0 }}
              />
              <Text size="md" fw={600} truncate="end" style={{ minWidth: 0 }}>
                {record.name}
              </Text>
            </Group>

            {/* Right side: hover actions */}
            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
            </Group>
          </Group>

          {/* Content rows */}
          {contentRows}
        </Stack>
      </Group>
    </Box>
  );

  // ============================================
  // Render — swipeable on mobile, plain on desktop
  // ============================================

  const cardPaperProps = {
    p: "sm" as const,
    withBorder: true,
    bg: highlighted ? ("blue.0" as const) : undefined,
    opacity: isDeleting ? 0.5 : 1,
    className: CARD_CLASS,
    style: { transition: "box-shadow 150ms ease, opacity 150ms ease" },
    onClick: handleClick,
    tabIndex: onClick ? 0 : undefined,
    role: onClick ? ("button" as const) : undefined,
  };

  if (leftSwipeActions.length > 0) {
    return (
      <>
        {/* Mobile: swipeable */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={handleSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-default)"
          >
            <Paper {...cardPaperProps}>
              {mobileLayout}
            </Paper>
          </SwipeableCard>
        </Box>

        {/* Desktop: plain card with hover actions */}
        <Box visibleFrom="sm">
          <Paper {...cardPaperProps}>
            {desktopLayout}
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <Paper {...cardPaperProps}>
      {mobileLayout}
      {desktopLayout}
    </Paper>
  );
};

const SavedPlaceCard = React.memo(SavedPlaceCardComponent);

// ============================================
// Main Component
// ============================================

const SavedPlacesListComponent: React.FC<SavedPlacesListProps> = ({
  records: recordsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onRecordClick,
  deletingId: deletingIdProp,
  highlightedId,
  onAdd,
  selectable = false,
  searchKeyword,
}) => {
  const { t } = useTranslation(["savedPlaces", "common"]);

  // ── Context vs Props resolution ──
  const savedPlacesContext = useOptionalSavedPlacesContext();

  const records = recordsProp ?? savedPlacesContext?.savedPlaces ?? [];
  const loading = loadingProp ?? savedPlacesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? savedPlacesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? savedPlacesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? savedPlacesContext?.loadMore;
  const deletingId = deletingIdProp ?? savedPlacesContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: records.length });

  // ── Selection ──

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleSelect = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;
      const newSelection = checked
        ? [...selectedIds, id]
        : selectedIds.filter((sid) => sid !== id);
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange],
  );

  // ── Card interaction handlers ──

  const handleRecordClick = useCallback(
    (record: SavedPlace) => {
      closeAllCards();
      onRecordClick?.(record);
    },
    [onRecordClick, closeAllCards],
  );

  const handleEdit = useCallback(
    (record: SavedPlace) => {
      closeAllCards();
      onEdit?.(record);
    },
    [onEdit, closeAllCards],
  );

  const handleDelete = useCallback(
    (record: SavedPlace) => {
      closeAllCards();
      onDelete?.(record);
    },
    [onDelete, closeAllCards],
  );

  // ── Swipe state ──

  const handleSwipeStateChange = useCallback(
    (recordId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(recordId);
      }
    },
    [openCard],
  );

  // ── Loading state ──

  if (loading && records.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && records.length === 0) {
    // Filtered empty (search keyword active but no results)
    if (searchKeyword) {
      return (
        <Paper withBorder p="xl">
          <EmptyState
            title={t("savedPlaces:emptyState.filteredTitle", { defaultValue: "No matching places" })}
            description={t("savedPlaces:emptyState.filteredDescription", {
              defaultValue: "Try adjusting your search to see more results.",
            })}
            iconName="MapPinOff"
            size="sm"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("savedPlaces:emptyState.title", { defaultValue: "No saved places yet" })}
          description={t("savedPlaces:emptyState.description", {
            defaultValue: "Save your frequently visited locations to quickly fill in addresses when logging trips, expenses, and refuels.",
          })}
          iconName="MapPin"
          actionLabel={t("savedPlaces:emptyState.actionLabel", { defaultValue: "Add Place" })}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  // ── List ──

  return (
    <Stack gap="sm">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {records.map((record) => (
            <motion.div
              key={record.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <SavedPlaceCard
                record={record}
                selected={selectedSet.has(record.id || "")}
                onSelect={handleSelect}
                onClick={handleRecordClick}
                onEdit={onEdit ? handleEdit : undefined}
                onDelete={onDelete ? handleDelete : undefined}
                selectable={selectable}
                highlighted={record.id === highlightedId}
                isDeleting={deletingId === record.id}
                forceClose={shouldForceClose(record.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(record.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(record.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const SavedPlacesList = React.memo(SavedPlacesListComponent);